import { Link } from "react-router-dom";
import { useState } from "react";
import { useSettings } from "context/SettingsContext";
import {
  BadgeCheck,
  Clock3,
  CreditCard,
  FileText,
  PackageCheck,
  ShieldCheck,
  Truck,
  WalletCards,
  MapPinned,
  Headphones,
  Scale,
  LockKeyhole,
} from "lucide-react";

const summaryCards = [
  {
    title: "Orders",
    text: "Subject to availability, confirmation, and the right to cancel in case of errors or fraud concerns.",
    icon: PackageCheck,
    accent: "from-sky-500 to-cyan-400",
  },
  {
    title: "Payment",
    text: "All prices are shown in BDT and we currently accept Cash on Delivery and Online Payments.",
    icon: WalletCards,
    accent: "from-emerald-500 to-teal-400",
  },
  {
    title: "Delivery",
    text: "Estimated delivery time is 1-3 business days, depending on location and courier conditions.",
    icon: Truck,
    accent: "from-amber-500 to-orange-400",
  },
];

const policySections = [
  {
    id: "orders",
    title: "1. Orders",
    icon: PackageCheck,
    points: [
      "All orders are subject to product availability and acceptance by Nabil Fashion.",
      "After placing an order, you will receive an order confirmation. This confirmation does not guarantee acceptance of your order.",
      "We reserve the right to cancel or refuse any order due to product unavailability.",
      "If an order is cancelled after payment has been made, the applicable refund will be processed according to our Refund Policy.",
    ],
  },
  {
    id: "pricing",
    title: "2. Pricing & Payment",
    icon: CreditCard,
    points: [
      "All prices displayed on our website are in Bangladeshi Taka (BDT).",
      "Prices include applicable taxes unless otherwise stated.",
      "We currently accept Cash on Delivery (COD).",
      "Prices and promotional offers may change at any time without prior notice.",
    ],
  },
  {
    id: "shipping",
    title: "3. Shipping & Delivery",
    icon: Truck,
    points: [
      "Orders are generally delivered within 1-3 business days, depending on delivery location.",
      "Delivery charges, if applicable, will be displayed during checkout.",
      "Customers are requested to provide accurate shipping information to avoid delivery issues.",
      "Delivery times are estimated and may be delayed due to weather, holidays, or courier-related issues.",
    ],
  },
  {
    id: "returns",
    title: "4. Returns & Refunds",
    icon: BadgeCheck,
    points: [
      "If you receive a damaged, defective, or incorrect item, please contact us within 48 hours of receiving your order.",
      "Products must be unused and returned in their original packaging.",
      "Used, damaged, or incomplete items are not eligible for return or refund.",
      "Once the returned item has been inspected and approved, refunds will be processed within 7-14 business days.",
    ],
  },
  {
    id: "product-info",
    title: "5. Product Information",
    icon: FileText,
    points: [
      "We work hard to ensure that all product descriptions, images, specifications, and pricing are accurate.",
      "Product colors may vary slightly depending on your device's display settings.",
      "Packaging or product appearance may occasionally change due to manufacturer updates.",
      "Minor differences that do not affect product functionality are not considered defects.",
    ],
  },
  {
    id: "accounts",
    title: "6. User Accounts",
    icon: LockKeyhole,
    points: [
      "Nabil Fashion is not responsible for any orders, changes, or requests made using the phone number you provide.",
      "Please contact us immediately if you believe an order has been placed using your phone number without your permission.",
    ],
  },
  {
    id: "ip",
    title: "7. Intellectual Property",
    icon: ShieldCheck,
    points: [
      "All content on the Nabil Fashion website, including logos, images, text, and graphics, is the property of Nabil Fashion and may not be copied or used without permission.",
      "No content may be copied, reproduced, distributed, or used for commercial purposes without prior written permission.",
    ],
  },
  {
    id: "liability",
    title: "8. Limitation of Liability",
    icon: Scale,
    points: [
      "Nabil Fashion is not responsible for any indirect or unexpected damages resulting from the use of our website, products, or services.",
      "Our total liability shall not exceed the amount paid for the affected order.",
    ],
  },
  {
    id: "privacy",
    title: "9. Privacy",
    icon: Headphones,
    points: [
      "Your privacy is important to us.",
      "Your personal information is kept secure and used only for order processing, customer support, and improving our services.",
    ],
  },
  {
    id: "changes",
    title: "10. Changes to These Terms",
    icon: Clock3,
    points: [
      "We reserve the right to update or modify these Terms & Conditions at any time.",
      "Any changes will become effective immediately upon publication on this website.",
      "Continued use of our website after any updates constitutes your acceptance of the revised Terms.",
    ],
  },
  {
    id: "contact",
    title: "11. Contact Us",
    icon: MapPinned,
    points: [
      "If you have any questions regarding these Terms & Conditions or need assistance with your order, please contact our customer support team.",
      "We're always here to help and ensure you have a smooth shopping experience.",
    ],
  },
];

const TermsCondition = () => {
  const { theme } = useSettings();
  const isDark = theme === "dark";
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div
      className={`relative overflow-hidden select-none ${
        isDark ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-900"
      }`}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 left-0 h-72 w-72 rounded-full bg-sky-500/15 blur-3xl" />
        <div className="absolute top-28 right-0 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-amber-400/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-2 sm:px-5 md:px-6 md:py-10">
        <section
          className={`overflow-hidden rounded-3xl border shadow-2xl ${
            isDark
              ? "border-white/10 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white"
              : "border-white bg-gradient-to-br from-slate-900 via-sky-900 to-slate-800 text-white"
          }`}
        >
          <div className="px-4 py-4 sm:px-6 md:px-10 md:py-14">
            <div className="mx-auto max-w-4xl text-center">
              {/*<p className="text-[11px] font-semibold uppercase tracking-[0.34em] text-cyan-300">*/}
              {/*  Legal Information*/}
              {/*</p>*/}
              <h1 className="md:mt-3 mt-2 text-2xl font-bold leading-tight sm:text-4xl md:text-5xl">
                Terms & Conditions
              </h1>
              <p className="md:mt-4 mt-2 text-xs leading-6 text-slate-200 sm:text-sm md:text-base md:leading-7">
                Welcome to Nabil Fashion. By using our website or placing an
                order, you agree to these Terms & Conditions. Please read them
                carefully before using our services.
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3 text-xs text-slate-100">
                <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2">
                  BDT pricing
                </span>
                <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2">
                  1-3 business day delivery
                </span>
                {/*<span className="rounded-full border border-white/15 bg-white/10 px-4 py-2">*/}
                {/*  48 hour return window*/}
                {/*</span>*/}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-6 md:mt-10 md:grid gap-4 md:grid-cols-3 hidden ">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.title}
                className={`rounded-2xl border p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  isDark
                    ? "border-white/10 bg-white/5"
                    : "border-slate-200 bg-white"
                }`}
              >
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${card.accent} text-white shadow-lg`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <h2 className="mt-4 text-lg font-semibold">{card.title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  {card.text}
                </p>
              </div>
            );
          })}
        </section>

        <section className="mt-6 md:mt-10 ">
          {/*<div className="space-y-4">*/}
          {/*  {policySections.map((section) => {*/}
          {/*    const Icon = section.icon;*/}
          {/*    return (*/}
          {/*      <article*/}
          {/*        id={section.id}*/}
          {/*        key={section.title}*/}
          {/*        className={`rounded-3xl border p-5 md:p-6 ${*/}
          {/*          isDark*/}
          {/*            ? "border-white/10 bg-slate-900/80"*/}
          {/*            : "border-slate-200 bg-white"*/}
          {/*        } shadow-sm`}*/}
          {/*      >*/}
          {/*        <div className="flex items-start gap-3">*/}
          {/*          <div className="mt-0.5 flex h-10 w-10 flex-none items-center justify-center rounded-2xl bg-slate-100 text-slate-900 dark:bg-white/10 dark:text-white">*/}
          {/*            <Icon className="h-5 w-5" />*/}
          {/*          </div>*/}
          {/*          <div className="min-w-0">*/}
          {/*            <h2 className="text-base font-semibold md:text-lg">*/}
          {/*              {section.title}*/}
          {/*            </h2>*/}
          {/*          </div>*/}
          {/*        </div>*/}

          {/*        <div className="mt-4 space-y-3">*/}
          {/*          {section.points.map((point) => (*/}
          {/*            <div*/}
          {/*              key={point}*/}
          {/*              className={`flex gap-3 rounded-2xl p-3 ${*/}
          {/*                isDark ? "bg-white/5" : "bg-slate-50"*/}
          {/*              }`}*/}
          {/*            >*/}
          {/*              <div className="mt-1 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">*/}
          {/*                <BadgeCheck className="h-3.5 w-3.5" />*/}
          {/*              </div>*/}
          {/*              <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">*/}
          {/*                {point}*/}
          {/*              </p>*/}
          {/*            </div>*/}
          {/*          ))}*/}
          {/*        </div>*/}
          {/*      </article>*/}
          {/*    );*/}
          {/*  })}*/}
          {/*</div>*/}

          <aside className="lg:sticky lg:top-6 h-fit">
            <div
              className={`mb-16 rounded-3xl border p-5 md:p-6 ${
                isDark
                  ? "border-white/10 bg-white/5"
                  : "border-slate-200 bg-white shadow-sm"
              }`}
            >
              {/*<p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-500">*/}
              {/*  Quick Index*/}
              {/*</p>*/}
              {/*<p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-300">*/}
              {/*  Jump to a section below for a faster read.*/}
              {/*</p>*/}

              <div className="mt-2 space-y-2">
                {policySections.map((section, index) => {
                  const isOpen = openIndex === index;
                  const Icon = section.icon;
                  return (
                    <div
                      id={section.id}
                      key={section.title}
                      className={`overflow-hidden rounded-2xl border transition ${
                        isDark
                          ? "border-white/10 bg-slate-900/70"
                          : "border-slate-200 bg-slate-50"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenIndex((current) =>
                            current === index ? -1 : index,
                          )
                        }
                        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm transition hover:bg-black/5 dark:hover:bg-white/5"
                        aria-expanded={isOpen}
                        aria-controls={`quick-index-panel-${section.id}`}
                      >
                        <span className="pr-3 font-medium text-slate-700 dark:text-slate-100">
                          {section.title}
                        </span>
                        <span className="text-slate-400">
                          {isOpen ? "-" : "+"}
                        </span>
                      </button>

                      <div
                        id={`quick-index-panel-${section.id}`}
                        className={`grid transition-all duration-300 ease-out ${
                          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                        }`}
                      >
                        {/*<div className="overflow-hidden">*/}
                        {/*  <div className="border-t border-slate-200 px-4 py-3 text-sm leading-6 text-slate-500 dark:border-white/10 dark:text-slate-300">*/}
                        {/*    {section.points[0]}*/}
                        {/*  </div>*/}
                        {/*</div>*/}
                        <div className="overflow-hidden">
                          <div className="my-3">
                            {section.points.map((point) => (
                              <div
                                key={point}
                                className={`flex gap-3 rounded-2xl px-3 py-1 ${
                                  isDark ? "bg-white/5" : "bg-slate-50"
                                }`}
                              >
                                <div className="mt-1 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">
                                  <BadgeCheck className="h-3.5 w-3.5" />
                                </div>
                                <p className="text-sm leading-6 text-justify text-slate-600 dark:text-slate-300">
                                  {point}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className={`mt-6  rounded-2xl p-4 ${
                  isDark ? "bg-slate-900/70" : "bg-slate-50"
                }`}
              >
                <p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-500">
                  Need help?
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-300">
                  If anything here is unclear, our support team can help explain
                  the order, delivery, or refund process.
                </p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/contactUs"
                    className="inline-flex items-center justify-center rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                  >
                    Contact Support
                  </Link>
                  <Link
                    to="/"
                    className={`inline-flex items-center justify-center rounded-full border px-4 py-2.5 text-sm font-semibold transition ${
                      isDark
                        ? "border-white/15 bg-white/5 text-white hover:bg-white/10"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    Back to Home
                  </Link>
                </div>
              </div>
            </div>
          </aside>
        </section>
      </div>
    </div>
  );
};

export default TermsCondition;
